import React from "react";

import CloseIcon from "@mui/icons-material/Close";
import RepeatIcon from "@mui/icons-material/Repeat";
import { Box, Tooltip, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { makeStyles } from "tss-react/mui";

import Interval from "../store/Interval";
import { formatMinutesRange, intervalMinutes } from "../utils/calendar";

export type BlockGestureMode = "move" | "resize-start" | "resize-end";

type Props = {
  interval: Interval;
  topPx: number;
  heightPx: number;
  isOverride: boolean;
  onGestureStart: (event: React.PointerEvent, mode: BlockGestureMode) => void;
  onDelete: () => void;
};

const CalendarEventBlock: React.FC<Props> = observer(
  ({ interval, topPx, heightPx, isOverride, onGestureStart, onDelete }) => {
    const { classes, cx } = useStyles();
    const minutes = intervalMinutes(interval);
    const hasError = !!interval.errorMessage;
    // Below this height the label would clip; hide it and lean on the tooltip.
    const compact = heightPx < 34;

    return (
      <Tooltip
        title={interval.errorMessage ?? ""}
        disableHoverListener={!hasError}
        arrow
      >
        <div
          className={cx(
            classes.block,
            isOverride ? classes.override : classes.recurring,
            hasError && classes.error
          )}
          style={{ top: topPx, height: Math.max(heightPx, 18) }}
          onPointerDown={(event) => onGestureStart(event, "move")}
          role="button"
          aria-label="availability block"
        >
          <div
            className={cx(classes.handle, classes.handleTop)}
            onPointerDown={(event) => {
              event.stopPropagation();
              onGestureStart(event, "resize-start");
            }}
          />

          <Box className={classes.content}>
            {!compact && minutes && (
              <Typography className={classes.label} noWrap>
                {formatMinutesRange(minutes.start, minutes.end)}
              </Typography>
            )}
            {!isOverride && !compact && (
              <RepeatIcon className={classes.repeatIcon} />
            )}
          </Box>

          <button
            type="button"
            className={classes.delete}
            aria-label="remove availability block"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              onDelete();
            }}
          >
            <CloseIcon className={classes.deleteIcon} />
          </button>

          <div
            className={cx(classes.handle, classes.handleBottom)}
            onPointerDown={(event) => {
              event.stopPropagation();
              onGestureStart(event, "resize-end");
            }}
          />
        </div>
      </Tooltip>
    );
  }
);

const useStyles = makeStyles()(() => ({
  block: {
    position: "absolute",
    left: 2,
    right: 2,
    borderRadius: 6,
    borderStyle: "solid",
    borderWidth: 1,
    padding: "2px 6px",
    boxSizing: "border-box",
    cursor: "grab",
    overflow: "hidden",
    touchAction: "none",
    userSelect: "none",
    "&:active": { cursor: "grabbing" },
    "&:hover $delete": { opacity: 1 },
  },
  recurring: {
    backgroundColor: "#E8EEFF",
    borderColor: "#3D6DF0",
    color: "#20305C",
  },
  override: {
    backgroundColor: "#E7F6EC",
    borderColor: "#2E9E5B",
    color: "#1C5C36",
  },
  error: {
    backgroundColor: "#FDECEC",
    borderColor: "#E5484D",
    color: "#8A1C1F",
  },
  content: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 4,
    height: "100%",
  },
  label: {
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1.3,
  },
  repeatIcon: {
    fontSize: 12,
    opacity: 0.7,
    flexShrink: 0,
  },
  handle: {
    position: "absolute",
    left: 0,
    right: 0,
    height: 6,
    cursor: "ns-resize",
  },
  handleTop: { top: 0 },
  handleBottom: { bottom: 0 },
  delete: {
    position: "absolute",
    top: 2,
    right: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    height: 16,
    width: 16,
    borderRadius: 4,
    border: "none",
    cursor: "pointer",
    backgroundColor: "rgba(255,255,255,0.7)",
    color: "inherit",
    opacity: 0,
    transition: "opacity 120ms ease",
  },
  deleteIcon: {
    fontSize: 12,
  },
}));

export default CalendarEventBlock;
